import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { chatService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Chat = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    // Si llegamos con ?conversation=ID, abrir esa conversación
    const convId = searchParams.get('conversation');
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => String(c.id) === String(convId));
      if (conv) {
        handleSelectConversation(conv);
      }
    }
    // eslint-disable-next-line
  }, [conversations, searchParams]);

  useEffect(() => {
    // Scroll automático al final de los mensajes
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await chatService.getConversations();
      setConversations(res.data);
    } catch (err) {
      setError('No se pudieron cargar las conversaciones.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setMessages([]);
    setMsgLoading(true);
    try {
      const res = await chatService.getMessages(conv.id);
      setMessages(res.data);
    } catch (err) {
      setError('No se pudieron cargar los mensajes.');
    } finally {
      setMsgLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedConversation) return;
    setMsgLoading(true);
    try {
      await chatService.sendMessage(selectedConversation.id, { content: message });
      setMessage('');
      // Recargar mensajes
      const res = await chatService.getMessages(selectedConversation.id);
      setMessages(res.data);
    } catch (err) {
      setError('No se pudo enviar el mensaje.');
    } finally {
      setMsgLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">Debes iniciar sesión para acceder al chat.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 2, display: 'flex', minHeight: 500 }}>
        {/* Lista de conversaciones */}
        <Box sx={{ width: 280, borderRight: '1px solid #e5e7eb', pr: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Conversaciones
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <List>
              {conversations.length === 0 && (
                <ListItem>
                  <ListItemText primary="Sin conversaciones" />
                </ListItem>
              )}
              {conversations.map((conv) => (
                <ListItem
                  button
                  key={conv.id}
                  selected={selectedConversation && conv.id === selectedConversation.id}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <ListItemText
                    primary={conv.participant_username || 'Usuario'}
                    secondary={conv.last_message ? conv.last_message.content.slice(0, 30) : 'Sin mensajes'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        {/* Mensajes */}
        <Box sx={{ flex: 1, pl: 3, display: 'flex', flexDirection: 'column' }}>
          {selectedConversation ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Chat con {selectedConversation.participant_username || 'Usuario'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                {msgLoading ? (
                  <CircularProgress />
                ) : (
                  messages.map((msg) => (
                    <Box key={msg.id} sx={{ mb: 1, textAlign: msg.sender === user.id ? 'right' : 'left' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'inline-block',
                          bgcolor: msg.sender === user.id ? '#1e3a8a' : '#f3f4f6',
                          color: msg.sender === user.id ? 'white' : 'black',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          maxWidth: '70%'
                        }}
                      >
                        {msg.content}
                      </Typography>
                    </Box>
                  ))
                )}
                <div ref={messagesEndRef} />
              </Box>
              <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe un mensaje..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  disabled={msgLoading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={msgLoading || !message.trim()}
                >
                  Enviar
                </Button>
              </form>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Selecciona una conversación para comenzar
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Chat; 